import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { HelpCircle } from "lucide-react";

export const HELP_CONTENT = {
  "/": {
    title: "Role Board",
    sections: [
      {
        heading: "What you're looking at",
        body: "The Role Board is your command center. Each card represents a talent market search you've defined — the criteria, the matching pool size, and the current status.",
      },
      {
        heading: "Live pool counts",
        body: "The number on each card is live. It reflects how many candidates in our database match your hard-filter criteria right now. As our candidate pool grows, your counts grow too — without you changing a thing.",
      },
      {
        heading: "What makes this different",
        body: "These aren't job applicants. They're passive professionals who've described their ideal next role through Varyynt's consumer platform. You're seeing supply-side intent data that doesn't exist on LinkedIn, Biospace, or any job board.",
      },
      {
        heading: "Next step",
        body: "Click \u201cNew Role Search\u201d to define your criteria, or click into an existing search to run Deep Analysis and see AI-scored candidate matches.",
      },
    ],
  },
  "/search": {
    title: "Talent Market Explorer",
    sections: [
      {
        heading: "Left panel: Define your criteria",
        body: "Select seniority levels, functional areas, therapeutic areas, geography, and employer type. Each filter updates the live pool count instantly — you're querying our real candidate database in real time.",
      },
      {
        heading: "Hard filters vs. intent signals",
        body: "The top criteria are hard filters — they narrow the pool. The intent signals at the bottom (company type preference, work environment, role scope notes) don't change the count. Instead, they're used by Deep Analysis to score how well each candidate's career intent aligns with your opportunity.",
      },
      {
        heading: "JD parsing",
        body: "Paste a job description and click \u201cParse with AI\u201d to auto-fill criteria fields. The AI extracts seniority, function, therapeutic area, and geography from your JD text.",
      },
      {
        heading: "Deep Analysis",
        body: "Once you're satisfied with the pool, run Deep Analysis. Our AI scores every matching candidate on two axes: Experience Fit (structured match on title, function, TA, degree) and Intent Alignment (how closely their stated career preferences match what your role offers). This intent scoring is the core value — it surfaces candidates who aren't just qualified, but likely interested.",
      },
      {
        heading: "Selecting candidates",
        body: "After scoring, use the toolbar to filter by minimum score, select all above a threshold, or click individual cards. Then create a Work Order to initiate outreach.",
      },
    ],
  },
  "/work-orders": {
    title: "Work Orders",
    sections: [
      {
        heading: "What is a Work Order?",
        body: "A Work Order is your formal request to engage candidates. You select scored candidates from a search, choose an outreach type, and submit. The Varyynt team handles de-identified outreach on your behalf.",
      },
      {
        heading: "Two types",
        body: "Job Promotion sends a specific role description to selected candidates. De-Identified Outreach gauges interest without revealing your company — protecting confidentiality for sensitive searches.",
      },
      {
        heading: "Why this matters",
        body: "Traditional recruiting requires months of back-channel introductions and permission-seeking before you even know if a candidate is interested. Varyynt flips this: we already know what they want. The outreach confirms mutual interest before either side invests real time.",
      },
      {
        heading: "Tracking",
        body: "Monitor outreach progress, response rates, and interested counts right here. Expand any Work Order to see the full details and admin notes.",
      },
    ],
  },
  "/settings": {
    title: "Settings",
    sections: [
      {
        heading: "Profile",
        body: "Update your name, title, and LinkedIn URL. Change your password anytime.",
      },
      {
        heading: "Organization",
        body: "View your organization's name, contract tier, and suppression profile. Suppression ensures your own employees never appear in your search results.",
      },
      {
        heading: "Team management",
        body: "Org Admins can invite team members by email. Each invited user gets a 7-day registration link. Manage roles and deactivate accounts as needed.",
      },
    ],
  },
};

export const ADMIN_HELP_CONTENT = {
  "/admin/clients": {
    title: "Client Management",
    sections: [
      { heading: "Overview", body: "Manage all client organizations — view user counts, search volume, work order activity, and contract status at a glance." },
      { heading: "Demo vs. Paid", body: "Demo orgs explore the platform with flagged Work Orders that don't trigger real fulfillment. Use the Upgrade button to convert a demo client to paid when they sign a contract. This enables real outreach and removes the demo banner." },
      { heading: "Suppression profiles", body: "Each client has a suppression list of company names and email domains. Any candidate matching these is silently excluded from all search results for that client. This prevents clients from seeing their own employees. Use the tag input to add/remove entries, and the Test button to verify." },
      { heading: "Delegates", body: "Delegates are client_admin users who can invite their own team members. Promote a regular member to delegate status, or remove delegate access. This gives clients self-service team management." },
      { heading: "Demo invites", body: "Use \u201cInvite to Demo\u201d to onboard a sales prospect. This creates a demo org (or reuses one), creates an invited user, and sends a branded email. Demo invites are the top-of-funnel tool for enterprise sales." },
    ],
  },
  "/admin/work-orders": {
    title: "Work Order Queue",
    sections: [
      { heading: "Live vs. Demo", body: "The Live tab shows Work Orders from paid orgs that require real fulfillment — these need action. The Demo tab shows demo org submissions for review only." },
      { heading: "Fulfillment workflow", body: "Update status as you progress: Pending \u2192 In Progress \u2192 Outreach Sent \u2192 Responses Received \u2192 Completed. Each status change emails the client user who created the WO." },
      { heading: "Export", body: "Use Export CSV to download all Work Orders for offline handoff to the recruiting team. Includes client name, role title, search criteria, priority, and status." },
    ],
  },
  "/admin/candidates": {
    title: "Candidate Database",
    sections: [
      { heading: "Full PII access", body: "As Master Admin, you see all identified fields — names, LinkedIn URLs, current employers. This access is audit-logged. Every time you view this page or a candidate detail, an admin_pii_view event is recorded." },
      { heading: "Search", body: "Search by name, title, anonymous ID, or any field. Click a row to see the full candidate profile including raw LinkedIn data and intent summaries." },
    ],
  },
  "/admin/analytics": {
    title: "Activity & Analytics",
    sections: [
      { heading: "Daily Roll-up", body: "24-hour snapshot of platform activity — logins, searches, deep analyses, work orders. Auto-refreshes every 5 minutes." },
      { heading: "Login Log", body: "Filterable audit trail of all login events. Filter by org or event type. Export to CSV for compliance reporting." },
      { heading: "Activity Feed", body: "Real-time feed of all platform events with color-coded badges and metadata summaries. Shows who did what, when, and for which org." },
      { heading: "Platform Stats", body: "Candidate database distributions — seniority, functional area, geography, therapeutic areas. Use this to understand your candidate supply by segment." },
    ],
  },
};

export default function HelpDrawer({ pageKey }) {
  const content = HELP_CONTENT[pageKey] || ADMIN_HELP_CONTENT[pageKey];
  if (!content) return null;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-accent text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-testid="help-drawer-trigger">
          <HelpCircle className="w-4 h-4" />
          <span>Help</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[380px] sm:w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-slate-900 text-lg">{content.title}</SheetTitle>
          <SheetDescription className="text-sm text-slate-500">Contextual guidance for this page</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          {content.sections.map((s, i) => (
            <div key={i}>
              <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">{s.heading}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </div>
          ))}
          <div className="bg-lime-50 border border-lime-200 rounded-lg p-4 mt-4">
            <p className="text-xs font-mono uppercase tracking-widest text-lime-700 mb-1">The Varyynt Advantage</p>
            <p className="text-sm text-lime-800 leading-relaxed">Every candidate in this platform has described their ideal next role. This is proprietary intent data — forward indicators of what they want, not just backward indicators of where they've been. No recruiter, LinkedIn subscription, or job board gives you this.</p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
